import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { GlobalLoader } from './components/GlobalLoader';
import { BreadcrumbProvider } from './context/BreadcrumbContext';

import MarketingLayout from "./layouts/MarketingLayout";
import AuthLayout from "./layouts/AuthLayout";
import PortalLayout from "./layouts/PortalLayout";

import HomePage from "./pages/marketing/HomePage";
import ProductPage from "./pages/marketing/ProductPage";
import PricingPage from "./pages/marketing/PricingPage";
import AboutPage from "./pages/marketing/AboutPage";
import ContactPage from "./pages/marketing/ContactPage";
import PrivacyPage from "./pages/marketing/PrivacyPage";
import TermsPage from "./pages/marketing/TermsPage";
// import BlogPage from "./pages/marketing/BlogPage";
// import CareersPage from "./pages/marketing/CareersPage";
// import HelpCenterPage from "./pages/marketing/HelpCenterPage";
// import FeaturesPage from "./pages/marketing/FeaturesPage";

import LoginPage from "./pages/auth/LoginPage";
import SignupPage from "./pages/auth/SignupPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import MicrosoftLoginRedirect from "./pages/auth/MicrosoftLoginRedirect";
import GoogleLoginRedirect from "./pages/auth/GoogleLoginRedirect";
import OAuthEmailCallback from "./pages/auth/OAuthEmailCallback";

import DashboardPage from "./pages/portal/DashboardPage";
import LeadsPage from "./pages/portal/LeadsPage";
import LeadDetailPage from "./pages/portal/LeadDetailPage";
import SegmentDetailPage from "./pages/portal/SegmentDetailPage";
import CampaignsPage from "./pages/portal/CampaignsPage";
import CampaignBuilderPage from "./pages/portal/CampaignBuilderPage";
import CampaignDetailPage from "./pages/portal/CampaignDetailPage";
import AnalyticsPage from "./pages/portal/AnalyticsPage";
import SettingsPage from "./pages/portal/SettingsPage";
import UpgradePlanPage from "./pages/portal/UpgradePlanPage";
import SequencesPage from "./pages/portal/SequencesPage";
import SequenceDetailPage from "./pages/portal/SequenceDetailPage";
import WorkflowBuilderPage from "./pages/portal/WorkflowBuilderPage";
import WorkflowsPage from "./pages/portal/WorkflowsPage";
import RepliesPage from "./pages/portal/RepliesPage";
import SupportPage from "./pages/portal/SupportPage";
import CreateSupportTicketPage from "./pages/portal/CreateSupportTicketPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function BillingRedirect() {
  const [searchParams] = useSearchParams();
  const payment = searchParams.get("payment");
  const to = payment
    ? `/app/settings?tab=Billing&payment=${payment}`
    : "/app/settings?tab=Billing";
  return <Navigate to={to} replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BreadcrumbProvider>
      <TooltipProvider>
        <GlobalLoader />
        <Toaster richColors position="top-right" />
        <ScrollToTop />
        <Routes>
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            {/* <Route path="/blog" element={<BlogPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/help" element={<HelpCenterPage />} />
            <Route path="/features" element={<FeaturesPage />} /> */}
          </Route>
          <Route path="/microsoft-login" element={<MicrosoftLoginRedirect />} />
          <Route path="/google-login" element={<GoogleLoginRedirect />} />
          <Route path="/oauth/email/callback" element={<OAuthEmailCallback />} />
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>
          <Route element={<PortalLayout />}>
            <Route path="/app/dashboard" element={<DashboardPage />} />
            <Route path="/app/leads" element={<LeadsPage />} />
            <Route path="/app/leads/:id" element={<LeadDetailPage />} />
            <Route path="/app/segments/:id" element={<SegmentDetailPage />} />
            <Route path="/app/campaigns" element={<CampaignsPage />} />
            <Route path="/app/campaigns/new" element={<CampaignBuilderPage />} />
            <Route path="/app/campaigns/:id" element={<CampaignDetailPage />} />
            <Route path="/app/campaigns/:id/edit" element={<CampaignBuilderPage />} />
            <Route path="/app/sequences" element={<SequencesPage />} />
            <Route path="/app/sequences/new" element={<SequenceDetailPage />} />
            <Route path="/app/sequences/:id" element={<SequenceDetailPage />} />
            <Route path="/app/workflows" element={<WorkflowsPage />} />
            <Route path="/app/workflows/new" element={<WorkflowBuilderPage />} />
            <Route path="/app/workflows/:id/edit" element={<WorkflowBuilderPage />} />
            <Route path="/app/replies" element={<RepliesPage />} />
            <Route path="/app/support" element={<SupportPage />} />
            <Route path="/app/support/new" element={<CreateSupportTicketPage />} />
            <Route path="/app/analytics" element={<AnalyticsPage />} />
            <Route path="/app/upgrade-plan" element={<UpgradePlanPage />} />
            <Route path="/app/settings" element={<SettingsPage />} />
            <Route path="/app/billing" element={<BillingRedirect />} />
          </Route>
        </Routes>
      </TooltipProvider>
      </BreadcrumbProvider>
    </QueryClientProvider>
  );
}

export default App;
